import { createLoader } from "../../utils";
import { initExtractedTableCards } from "./table-cards";

// ===========================================================
//  Fix accordion max-height après injection de contenu fetché
//  Problème : accordion-content.open a max-height: 4000px mais
//  le contenu réel peut dépasser → overlap sur le reste de la page.
//  Solution : recalculer dynamiquement + observer les changements.
// ===========================================================
function fixAccordionMaxHeight(container) {
	const accContent = container.closest(".accordion-content");
	if (!accContent) return;

	const update = () => {
		if (accContent.classList.contains("open")) {
			accContent.style.maxHeight = accContent.scrollHeight + "px";
		}
	};

	// Léger délai pour laisser le layout se calculer (images, tables…)
	requestAnimationFrame(() => {
		update();
		// Continue à s'adapter si le contenu change :
		// pagination, switch Table↔Cards, filtres de recherche…
		new ResizeObserver(update).observe(container);
	});
}

// ===========================================================
//  Reformate les cellules de dates dans les tableaux fetchés
//
//  Table mode : deux lignes sans séparateur, aligné à gauche
//    2026-07-27
//    2026-07-31
//
//  Cards mode : idem mais text-align forcé à left
//  (le CSS cards centre le contenu des td, on corrige ici)
// ===========================================================
const DATE_RANGE_RE = /^(\d{4}-\d{2}-\d{2})\s*[-–]\s*(\d{4}-\d{2}-\d{2})$/;
const DATE_SINGLE_RE = /^\d{4}-\d{2}-\d{2}$/;

function fixTableDates(container) {
	container.querySelectorAll("td").forEach((td) => {
		const text = td.textContent.trim();
		const isCards =
			td.closest("table")?.getAttribute("data-ext-view") === "cards";

		if (DATE_RANGE_RE.test(text)) {
			const [, start, end] = text.match(DATE_RANGE_RE);
			td.innerHTML =
				`<span style="white-space:nowrap;display:block">${start}</span>` +
				`<span style="white-space:nowrap;display:block">${end}</span>`;
			td.style.whiteSpace = "nowrap";
			// Cards mode centre le contenu via CSS → on force left ici
			td.style.textAlign = isCards ? "left" : "";
		} else if (DATE_SINGLE_RE.test(text)) {
			td.style.whiteSpace = "nowrap";
			td.style.textAlign = "";
		}
	});
}

// ===========================================================
//  Auto-resolve optimal CSS selector based on known URL patterns.
//  Some ITU sites use generic class names (e.g. .container) that
//  match multiple elements — this map picks the right one.
//  If no pattern matches, the user-provided selector is kept as-is.
// ===========================================================
const SELECTOR_MAP = [
	// Order matters — more specific patterns first
	{ pattern: "itu.int/hub/tag/",          selector: ".post-archive" },
	{ pattern: "itu.int/hub/",              selector: "section.container" },
	{ pattern: "itu.int/md/",               selector: "tbody" },
	{ pattern: "itu.int/net4/ITU-R/events", selector: ".content" },
];

function resolveSelector(url, userSelector) {
	for (const entry of SELECTOR_MAP) {
		if (url.includes(entry.pattern)) return entry.selector;
	}
	return userSelector;
}

export function initExtractElement() {
	document
		.querySelectorAll(
			"#extracted-content-placeholder, .extracted-content-placeholder"
		)
		.forEach((extractContainer) => {
			const sourceUrl = extractContainer.getAttribute("data-url");
			const rawSelector = extractContainer.getAttribute("data-selector");

			// --- Auto-resolve optimal selector based on known URL patterns ---
			const selector = resolveSelector(sourceUrl, rawSelector || "");

			if (!sourceUrl || !selector) {
				extractContainer.innerHTML = `<p style="color:red">⚠️ Missing <code>data-url</code> or <code>data-selector</code>.</p>`;
				return;
			}

			// --- URL helpers ---
			function makeToAbs(baseUrl) {
				const base = new URL(baseUrl);
				return (u) => {
					if (!u || typeof u !== "string") return u;
					if (
						u.startsWith("#") ||
						u.startsWith("mailto:") ||
						u.startsWith("tel:") ||
						u.startsWith("data:") ||
						u.startsWith("http") ||
						u.startsWith("https")
					)
						return u;
					if (u.startsWith("/hub") || u.startsWith("/sites")) {
						return new URL(u, base.origin).href;
					}
					try {
						return new URL(u, base).href;
					} catch {
						return u;
					}
				};
			}

			function fixSrcset(val, toAbs) {
				if (!val) return val;
				return val
					.split(",")
					.map((part) => {
						const [urlPart, descriptor] = part.trim().split(/\s+/, 2);
						const abs = toAbs(urlPart);
						return descriptor ? `${abs} ${descriptor}` : abs;
					})
					.join(", ");
			}

			// --- Post-processing ---
			function postProcessFragment(fragment, toAbs, isInitialLoad) {
				// Fix images
				fragment.querySelectorAll("img").forEach((img) => {
					const dataSrc = img.getAttribute("data-src");
					const dataSrcset = img.getAttribute("data-srcset");
					const src = img.getAttribute("src");

					if (isInitialLoad) {
						if (!src && dataSrc) img.src = toAbs(dataSrc);
						if (src && src.startsWith("./")) img.src = toAbs(src);
					} else {
						if (dataSrc) {
							img.src = dataSrc;
							img.removeAttribute("data-src");
						}
						if (dataSrcset) {
							img.srcset = dataSrcset;
							img.removeAttribute("data-srcset");
						}
						img.classList.remove(
							"lazyload",
							"lazyloaded",
							"lazyload--processed"
						);
						const clone = img.cloneNode(true);
						img.replaceWith(clone);
					}
				});

				if (isInitialLoad) {
					fragment.querySelectorAll("source[srcset]").forEach((source) => {
						const srcset = source.getAttribute("srcset");
						if (srcset)
							source.setAttribute("srcset", fixSrcset(srcset, toAbs));
					});
				}

				// Fix links
				fragment.querySelectorAll("a[href]").forEach((a) => {
					a.href = toAbs(a.getAttribute("href"));
					a.target = "_blank";
					a.rel = "noopener noreferrer";
				});

				if (
					sourceUrl.includes("/ssf/") ||
					sourceUrl.includes("/hub/")
				) {
					fragment
						.querySelectorAll("a[target]")
						.forEach((a) => a.removeAttribute("target"));
				}

				// ITU SSF: data-bg → image
				fragment.querySelectorAll("[data-bg]").forEach((el) => {
					let bgUrl = el.getAttribute("data-bg");
					if (!bgUrl) return;
					bgUrl = bgUrl
						.trim()
						.replace(/^url\(["']?/, "")
						.replace(/["']?\)$/, "");
					if (el.querySelector("img")) return;
					const img = document.createElement("img");
					img.src = bgUrl;
					img.alt = el.getAttribute("aria-label") || "SSF image";
					img.loading = "lazy";
					img.decoding = "async";
					img.style.width = "100%";
					img.style.height = "auto";
					img.style.display = "block";
					el.prepend(img);
					el.classList.add("has-img");
				});

				// Clickable cards
				fragment
					.querySelectorAll("article.card-linked")
					.forEach((article) => {
						const url = article.getAttribute("data-url-single");
						if (url) {
							article.style.cursor = "pointer";
							article.addEventListener("click", (e) => {
								if (!e.target.closest("a"))
									window.open(url, "_blank");
							});
						}
					});
			}

			// --- Lazy image force ---
			function forceLazyImages(fragment) {
				fragment.querySelectorAll("img").forEach((img) => {
					const dataSrc = img.getAttribute("data-src");
					const dataSrcset = img.getAttribute("data-srcset");
					if (dataSrc) {
						img.src = dataSrc;
						img.removeAttribute("data-src");
					}
					if (dataSrcset) {
						img.srcset = dataSrcset;
						img.removeAttribute("data-srcset");
					}
					img.classList.remove(
						"lazyload",
						"lazyloaded",
						"lazyload--processed"
					);
					img.loading = "eager";
					img.decoding = "async";
				});

				// Removed: window.dispatchEvent(scroll/resize) — these were
				// crashing itu-main.js updateNav() which expects a DOM element
				// that doesn't exist in extracted content. The lazy images are
				// already force-loaded by the explicit src assignments above
				// and the setTimeout fallback below.

				setTimeout(() => {
					fragment.querySelectorAll("img[data-src]").forEach((img) => {
						const dataSrc = img.getAttribute("data-src");
						const src = img.getAttribute("src") || "";
						const isPlaceholder = /blank|spacer|lazy|transparent/i.test(
							src
						);
						if (dataSrc && (isPlaceholder || !src || src === "#")) {
							img.src = dataSrc;
							img.removeAttribute("data-src");
						}
					});
				}, 300);

				fragment.querySelectorAll("img[data-srcset]").forEach((img) => {
					const val = img.getAttribute("data-srcset");
					if (val) {
						img.setAttribute("srcset", val);
						img.removeAttribute("data-srcset");
					}
				});
			}

			// --- Decode HTML buffer ---
			function decodeBuffer(buffer) {
				let html = new TextDecoder("utf-8").decode(buffer);
				if (html.includes("\ufffd")) {
					try {
						html = new TextDecoder("iso-8859-1").decode(buffer);
					} catch {}
				}
				return html;
			}

			// --- Load page ---
			function loadPage(newUrl, isInitialLoad = false) {
				if (extractContainer.dataset.loading === "1") return;
				extractContainer.dataset.loading = "1";

				const loader = createLoader(
					extractContainer,
					isInitialLoad
						? "Loading in progress..."
						: "Loading more results..."
				);

				fetch(newUrl)
					.then((res) => res.arrayBuffer())
					.then((buffer) => {
						try {
							loader.remove();
						} catch {}
						extractContainer.dataset.loading = "0";

						const html = decodeBuffer(buffer);
						const parser = new DOMParser();
						const doc = parser.parseFromString(html, "text/html");
						const target = doc.querySelector(selector);

						if (!target) {
							extractContainer.innerHTML = `<p style="color:red">⚠️ Element not found: <code>${selector}</code></p>`;
							return;
						}

						const fragment = target.cloneNode(true);
						const toAbs = makeToAbs(newUrl);

						postProcessFragment(fragment, toAbs, isInitialLoad);

						extractContainer.innerHTML = "";

						// Reset tcInit so table-cards post-processing
						// (including hub cards) runs again on pagination
						delete extractContainer.dataset.tcInit;

						extractContainer.appendChild(fragment);

						if (isInitialLoad) {
							// Sort contacts by priority
							fragment
								.querySelectorAll(
									'.br-contact-group[data-sort-by="priority1"]'
								)
								.forEach((group) => {
									const cards = Array.from(
										group.querySelectorAll(".br-contact-card")
									);
									cards.sort((a, b) => {
										const pa =
											parseInt(
												a.getAttribute("data-priority1")
											) || 0;
										const pb =
											parseInt(
												b.getAttribute("data-priority1")
											) || 0;
										return pb - pa;
									});
									cards.forEach((card) =>
										group.appendChild(card)
									);
								});

							forceLazyImages(fragment);

							// Fire webcast event
							document.dispatchEvent(
								new CustomEvent("webcastLoaded", {
									detail: {
										container: extractContainer,
										url: newUrl,
									},
								})
							);
						}

						// 🃏 Enhance native <table> with Table/Cards switch
						initExtractedTableCards(extractContainer);

						// 📅 Reformater les dates (no-wrap + layout propre)
						fixTableDates(extractContainer);

						// 📐 Fix accordion max-height si le contenu fetché
						// est imbriqué dans un accordion-content (overlap fix)
						fixAccordionMaxHeight(extractContainer);

						if (!isInitialLoad) {
							extractContainer.scrollIntoView({
								behavior: "smooth",
								block: "start",
							});
						}
					})
					.catch((err) => {
						try {
							loader.remove();
						} catch {}
						extractContainer.dataset.loading = "0";
						extractContainer.innerHTML = `<p style="color:red">❌ Failed to load content: ${err.message}</p>`;
					});
			}

			// Pagination AJAX
			extractContainer.addEventListener("click", (e) => {
				const link = e.target.closest(".pagination a.page-numbers");
				if (!link) return;
				e.preventDefault();
				const newUrl = link.getAttribute("href");
				if (newUrl) loadPage(newUrl, false);
			});

			// Initial load
			loadPage(sourceUrl, true);
		});
}

/* ===========================================================
   Calendar icon replacement (runs after extract loads)
   =========================================================== */
export function replaceCalendarIcons() {
	const container = document.getElementById(
		"extracted-content-placeholder"
	);
	if (!container) return;

	setTimeout(() => {
		container
			.querySelectorAll('img[src*="PublishingImages/ev_ol.gif"]')
			.forEach((img) => {
				const link = img.closest("a");
				if (link) {
					const ituBtn = document.createElement("button");
					ituBtn.textContent = "Add to Calendar...";
					ituBtn.className = "ituBtn";
					ituBtn.addEventListener("click", (e) => {
						e.stopPropagation();
						window.open(
							link.href,
							"_blank",
							"noopener,noreferrer"
						);
					});
					link.replaceWith(ituBtn);
				}
			});

		container.querySelectorAll("td.ms-vb").forEach((td) => {
			td.innerHTML = td.innerHTML
				.replace(/Â\s*&nbsp;?/g, "")
				.trim();
		});
	}, 1000);
}